import React from 'react'

/**
 * A monogram tile standing in for an institution's logo.
 *
 * Plaid can return real brand logos, but only via `include_optional_metadata`
 * on the institutions endpoint, and we don't persist them today (PlaidConnection
 * stores name + id only). Until that lands, a deterministic monogram beats a
 * generic bank emoji: it's stable per institution, distinguishes Chase from
 * Schwab at a glance, and is the same fallback most finance apps ship.
 *
 * Swap the innards for an <img> once a `logo` field exists on PlaidInstitution.
 */

// Hues chosen to stay legible on the dark surface at 18% fill / 90% text
// lightness. Deliberately muted — these are wayfinding aids, not brand colors.
const HUES = [204, 262, 158, 22, 340, 190, 44, 280]

/**
 * Map an institution name onto a stable hue.
 *
 * Simple sum-of-chars hash: we only need determinism and rough spread across
 * a handful of institutions, not cryptographic distribution.
 */
const hueFor = (name: string): number => {
  let total = 0
  for (let i = 0; i < name.length; i += 1) total += name.charCodeAt(i)
  return HUES[total % HUES.length]
}

/**
 * Pull up to two initials out of an institution name.
 *
 * Drops corporate filler ("Bank of America" -> "BA", not "BOA") so the tile
 * stays readable at 36px.
 */
const initialsFor = (name: string): string => {
  const skip = new Set(['of', 'the', 'and', '&', 'na', 'inc', 'llc'])
  const words = name
    .replace(/[^\w\s&]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !skip.has(w.toLowerCase()))

  if (words.length === 0) return '?'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

export interface InstitutionMarkProps {
  name?: string | null
  size?: number
}

const InstitutionMark: React.FC<InstitutionMarkProps> = ({ name, size = 36 }) => {
  const trimmed = name?.trim() ?? ''
  // The accessible label and the glyph come from different places on purpose:
  // an unnamed institution should read as "Unknown institution" to a screen
  // reader but render a neutral placeholder, not initials of the fallback text.
  const label = trimmed || 'Unknown institution'
  const initials = trimmed ? initialsFor(trimmed) : '?'
  const hue = hueFor(label)

  return (
    <span
      className="institution-mark"
      style={{
        width: size,
        height: size,
        // Sized against the tile so one component works at 24px and 44px.
        fontSize: Math.round(size * 0.36),
        background: `hsl(${hue} 62% 52% / 0.16)`,
        color: `hsl(${hue} 72% 78%)`,
        borderColor: `hsl(${hue} 62% 60% / 0.28)`,
      }}
      role="img"
      aria-label={label}
    >
      {initials}
    </span>
  )
}

export default InstitutionMark
